import React from 'react';
import StylusFrame from './StylusFrame';
import FrameDrawingBrush, {
  type StylusFrameOptions,
} from './FrameDrawingBrush';
import Frame from '../objects/Frame';
import icon from '../assets/icons/frameMenuCustomFrameIcon.svg';
import { svgToIcon } from '../utils/svg';

type ratios = '16-9' | '1-1' | 'custom';

// Base class for frame ratio variants
abstract class StylusFrameRatio extends StylusFrame {
  abstract ratio(): ratios;
  abstract description(): string;

  stylusId() {
    return `frame-${this.ratio()}`;
  }

  // Calculate width based on ratio. Height always stays the same
  calculateDimensions(): { width: number } {
    const baseWidth = Frame.defaultWidth();
    const baseHeight = Frame.defaultHeight();

    const ratio = this.ratio();
    switch (ratio) {
      case '16-9':
        return { width: baseHeight * (16 / 9) };
      case '1-1':
        return { width: baseHeight };
      case 'custom':
        return { width: baseWidth };
      default: {
        const _exhaustivenessCheck: never = ratio;
        return _exhaustivenessCheck;
      }
    }
  }

  activate() {
    const dimensions = this.calculateDimensions();
    let uniformScaling = true;
    if (this.ratio() === 'custom') {
      uniformScaling = false; // Custom frames allow independent width/height scaling
    }
    const options: StylusFrameOptions = {
      width: dimensions.width,
      uniformScaling,
    };

    const brush = new FrameDrawingBrush(this.controller.canvas, this, options);
    this.controller.canvas.isDrawingMode = true;
    this.controller.canvas.freeDrawingBrush = brush;
  }
}

// Custom frame (no specific ratio)
export class StylusFrameCustom extends StylusFrameRatio {
  ratio(): ratios {
    return 'custom';
  }

  renderIcon() {
    // Align with the items in the frame menu that use fonts rather than svg
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '28px' }}>
        {svgToIcon(icon)}
      </div>
    );
  }

  description() {
    return 'Custom';
  }
}

// Wide frame (16:9)
export class StylusFrameWide extends StylusFrameRatio {
  ratio(): ratios {
    return '16-9';
  }

  iconClass() {
    return 'fa-regular fa-rectangle-wide';
  }

  description() {
    return '16 : 9';
  }
}

// Square frame (1:1)
export class StylusFrameSquare extends StylusFrameRatio {
  ratio(): ratios {
    return '1-1';
  }

  iconClass() {
    return 'fa-regular fa-square';
  }

  description() {
    return '1 : 1';
  }
}

export default StylusFrameRatio;
